import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "@/hooks/useContent";
import api from "@/lib/api";
import { ProfileBars } from "@/components/ProfileBars";
import { ActionItem } from "@/components/ActionItem";
import { WhatsAppBox } from "@/components/WhatsAppBox";
import { PillarIcon } from "@/components/PillarIcon";
import { ArrowRight, Sparkles } from "lucide-react";

export function Result() {
  const { data: content } = useContent();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [actions, setActions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([api.get("/me/profile"), api.get("/me/state")]);
        if (!p.data.has_test) { navigate("/test", { replace: true }); return; }
        setProfile(p.data);
        setActions(s.data.actions || {});
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const toggle = async (id) => {
    const done = !actions[id];
    setActions((a) => ({ ...a, [id]: done }));
    try { await api.post("/actions/toggle", { action_id: id, done }); } catch {}
  };

  if (loading || !profile || !content) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#00000000]">
        <div className="w-10 h-10 rounded-full border-4 border-[#6B2247] border-t-[#E32C8C] animate-spin" />
      </div>
    );
  }

  const pri = content.pillars[profile.priority];
  const arch = profile.archetype;

  return (
    <div className="min-h-[100dvh] bg-[#00000000]">
      <div className="max-w-md md:max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="text-center mb-4 bw-fade">
          <p className="text-xs uppercase tracking-widest text-[#EAC6D9] font-semibold">Il tuo profilo</p>
          <h1 className="font-serif text-4xl font-bold text-[#F6DCEA] mt-1">Ecco da dove ripartire</h1>
        </div>

        {/* Sticky bars */}
        <div className="sticky top-2 z-30 bg-[#2A0A1B]/95 backdrop-blur-md p-4 rounded-2xl border border-[#E32C8C]/35 bw-card shadow-md mb-5">
          <ProfileBars profile={profile} pillars={content.pillars} pillarOrder={content.pillar_order} />
        </div>

        {/* Archetype card */}
        <div
          data-testid="archetype-card"
          className="rounded-2xl p-6 mb-5 text-white shadow-lg bw-fade"
          style={{ background: "linear-gradient(135deg,#E32C8C 0%,#8E1A52 55%,#3A0C21 100%)" }}
        >
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs uppercase tracking-widest font-semibold">Il tuo archetipo</span>
          </div>
          <h2 className="font-serif text-4xl font-bold mb-3" data-testid="archetype-name">{arch.name}</h2>
          <p className="text-[15px] leading-relaxed text-white/90 mb-4">{arch.description}</p>
          <div className="rounded-xl bg-white/15 p-4">
            <p className="text-xs uppercase tracking-wider font-semibold text-white/80 mb-1">Il tuo prossimo passo</p>
            <p className="text-[15px] leading-relaxed">{arch.next_step}</p>
          </div>
        </div>

        {/* Starting pillar */}
        <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-5">
          <div className="flex items-center gap-2 mb-2" style={{ color: pri.color }}>
            <PillarIcon pillar={profile.priority} className="w-5 h-5" />
            <h3 className="font-serif text-2xl font-semibold" style={{ color: "#F6DCEA" }}>
              Punto di partenza: {pri.name}
            </h3>
          </div>
          <p className="text-sm text-[#EAC6D9] italic mb-2">{pri.subtitle}</p>
          <p className="text-[15px] text-[#F6DCEA]/90 leading-relaxed">{pri.description}</p>
        </div>

        {/* Actions checklist */}
        <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-5">
          <h3 className="font-serif text-2xl font-semibold text-[#F6DCEA] mb-1">Per iniziare oggi</h3>
          <p className="text-sm text-[#EAC6D9] mb-4">5 piccoli gesti per il tuo {pri.name}</p>
          <div className="space-y-2">
            {pri.actions.map((label, i) => {
              const id = `${profile.priority}-${i}`;
              return (
                <ActionItem
                  key={id} label={label} checked={!!actions[id]}
                  onToggle={() => toggle(id)} testId={`result-action-${i}`} color={pri.color}
                />
              );
            })}
          </div>
        </div>

        {/* WhatsApp */}
        <div className="mb-5">
          <WhatsAppBox profile={profile} pillars={content.pillars} whatsappNumber={content.whatsapp_number} />
        </div>

        <button
          onClick={() => navigate("/app/home")}
          data-testid="go-home-btn"
          className="w-full bg-[#2A0A1B] text-[#F6DCEA] border border-[#E32C8C]/30 font-semibold py-3.5 px-6 rounded-full hover:bg-[#401431] transition-all flex items-center justify-center gap-2"
        >
          Vai alla tua Home <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
